import { isObject, keysToCamel, keysToSnake } from "@/helpers/humps";
import { safeSetItem } from "@/util/safeStorage";

export type APIErrors =
  | string[]
  | Record<string, string | string[]>
  | { message?: string };

export interface APIResponse<T = unknown> extends Response {
  // Original status; `status` is normalized to 200 on success.
  httpStatus?: number;
  data?: T;
  total?: number;
  errors?: APIErrors;
}

export interface ApiTokenResponse extends Response {
  token?: string;
  errors?: APIErrors;
}

export const API_BASE = (() => {
  if (typeof window !== "undefined") {
    return "/api/v2";
  }
  return `${process.env.API_HOST || ""}/api/v2`;
})();
export const TOKEN_NAME = "token-v2";

const isWorkerSSR = typeof window === "undefined";
const SSR_FETCH_TIMEOUT_MS = 2500;
const SSR_CACHE_TTL_S = 600;

type RequestConfig = Omit<RequestInit, "body"> & {
  body?: unknown;
  cf?: { cacheEverything?: boolean; cacheTtl?: number };
};

export async function callApi<T = unknown>(
  url: RequestInfo,
  config: RequestConfig = { headers: {} },
): Promise<APIResponse<T>> {
  // A caller-provided Authorization header (e.g. an SSO token) authenticates
  // the request itself — the stored session token must not replace it, and a
  // 401 rejecting it says nothing about the stored session's validity.
  const hasExplicitAuth = Boolean(
    (config?.headers as Record<string, string> | undefined)?.Authorization,
  );

  if (typeof window !== "undefined" && !hasExplicitAuth) {
    const token = localStorage.getItem(TOKEN_NAME) || null;
    if (token) {
      config.headers = { ...config.headers, Authorization: token };
    }
  }

  config.headers = {
    ...config.headers,
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  // SSR-only: apply timeout + edge cache hints. Don't override anything the
  // caller explicitly set, and don't touch browser fetches.
  if (isWorkerSSR) {
    if (!config.signal) {
      try {
        config.signal = AbortSignal.timeout(SSR_FETCH_TIMEOUT_MS);
      } catch {
        // AbortSignal.timeout is universally available on the workerd
        // runtime, but if a downstream environment lacks it we'd rather
        // skip the cap than break the call.
      }
    }
    if (!config.cf) {
      config.cf = {
        cacheEverything: true,
        cacheTtl: SSR_CACHE_TTL_S,
      };
    }
  }

  // If there is a body we need to parse and stringify it
  if (config?.body) {
    config.body = JSON.stringify(keysToSnake(config.body));
  }

  try {
    let resp: APIResponse<T>;
    try {
      resp = (await fetch(url, config as RequestInit)) as APIResponse<T>;
    } catch (networkError) {
      // fetch itself rejected — offline, blocked, or the page navigating away
      // mid-flight. WebKit's bare `TypeError: Load failed` carries no URL and
      // no stack, so if a caller lets it escape uncaught it reaches RUM as an
      // unattributable error. Wrap it so the endpoint is always identifiable.
      const wrapped = new Error(`API network failure for ${url}`);
      wrapped.name = "ApiNetworkError";
      (wrapped as Error & { cause?: unknown }).cause = networkError;
      throw wrapped;
    }

    if (typeof window !== "undefined") {
      const auth = resp.headers.get("authorization");
      if (auth) safeSetItem(TOKEN_NAME, auth);
    }

    if (resp.ok && resp.data) {
      const textData = await resp.text();
      if (textData) {
        const { data: d, total } = await JSON.parse(textData);
        resp.data = keysToCamel(d);
        resp.total = total;
      }
    } else {
      let respBody = await resp.text();
      let camelRespBody: Record<string, unknown> | null = null;
      if (respBody) {
        try {
          respBody = JSON.parse(respBody);
          camelRespBody = keysToCamel(respBody) as unknown as Record<
            string,
            unknown
          > | null;
          if (isObject(camelRespBody) || Array.isArray(camelRespBody)) {
            if (!resp.ok) {
              throw camelRespBody;
            }
            return {
              ...resp,
              ...camelRespBody,
              status: 200,
              ok: true,
              httpStatus: resp.status,
            } as APIResponse<T>;
          }
        } catch {}
      }
      if (!resp.ok) {
        const apiError: Record<string, unknown> | string =
          camelRespBody || respBody;
        // Stamp the HTTP status so callers can detect auth failures by status
        // (401) instead of brittle, locale-dependent message-string matching.
        if (apiError && typeof apiError === "object") {
          apiError.status = resp.status;
        }
        // An authenticated request was rejected: the stored token is dead.
        // Clear it so retry-on-failure callers can't loop forever re-sending a
        // token the API will only keep rejecting. The token guard leaves a
        // wrong-credentials 401 on sign_in (which carries no token) untouched.
        if (
          resp.status === 401 &&
          !hasExplicitAuth &&
          typeof window !== "undefined" &&
          localStorage.getItem(TOKEN_NAME)
        ) {
          localStorage.removeItem(TOKEN_NAME);
        }
        throw apiError;
      }
      return {
        ...resp,
        data: camelRespBody || respBody,
        status: 200,
        ok: true,
        httpStatus: resp.status,
      } as APIResponse<T>;
    }

    return resp;
  } catch (error: unknown) {
    const err = error as
      | { errors?: unknown[]; status?: number }
      | null
      | undefined;
    const firstError = err?.errors?.[0];
    if (
      !(
        typeof firstError === "string" &&
        firstError.includes("Your username or password is wrong")
      )
    ) {
      // 401s are expected auth-state noise (bad credentials, signed-out
      // session); warn keeps them visible in devtools but out of RUM errors.
      const log = err?.status === 401 ? console.warn : console.error;
      log(`API ERROR for ${url}`, error);
    }
    throw error;
  }
}
